import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiDownload, FiEdit3, FiPrinter, FiCheck } from 'react-icons/fi';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

const SalesOrderPDF = ({ isOpen, onClose, shed, dealer }) => {
  const [serialNumber, setSerialNumber] = useState(shed?.serialNumber || '');
  const [editingSerial, setEditingSerial] = useState(!shed?.serialNumber);
  const [generating, setGenerating] = useState(false);
  const serialInputRef = useRef(null);

  if (!isOpen || !shed) return null;

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://shedx.co';
  const checkoutUrl = `${siteUrl}/buy/${shed.id}`;

  const effectivePrice = shed.salePrice && shed.salePrice < shed.price ? shed.salePrice : shed.price;
  const depositAmount = shed.depositAmount || Math.round((effectivePrice || 0) * 0.25);

  const formatPrice = (price) => {
    if (!price) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0
    }).format(price);
  };

  const generatePDF = async () => {
    setGenerating(true);

    try {
      const qrDataUrl = await QRCode.toDataURL(checkoutUrl, {
        width: 200,
        margin: 1,
        color: { dark: '#000000', light: '#ffffff' }
      });

      const doc = new jsPDF('p', 'mm', 'letter');
      const pw = doc.internal.pageSize.getWidth();
      const margin = 18;
      let y = 20;

      // --- Header ---
      doc.setFillColor(16, 185, 129); // emerald-500
      doc.rect(0, 0, pw, 28, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('SALES ORDER', margin, 18);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      doc.text(dateStr, pw - margin, 12, { align: 'right' });
      doc.text(`Order #${shed.id?.slice(0, 8)?.toUpperCase() || 'N/A'}`, pw - margin, 18, { align: 'right' });

      y = 38;

      // --- Dealer info ---
      doc.setTextColor(60, 60, 60);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      const dealerName = dealer?.company_name || shed.dealerName || 'ShedX Dealer';
      doc.text(dealerName, margin, y);
      doc.setFont('helvetica', 'normal');
      if (shed.dealerLocation) {
        y += 4;
        doc.text(shed.dealerLocation, margin, y);
      }

      // --- QR Code (top right) ---
      const qrSize = 36;
      const qrX = pw - margin - qrSize;
      const qrY = 34;
      doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text('Scan to purchase', qrX + qrSize / 2, qrY + qrSize + 4, { align: 'center' });
      doc.text('this shed online', qrX + qrSize / 2, qrY + qrSize + 8, { align: 'center' });

      y = 56;

      // --- Divider ---
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pw - margin, y);
      y += 8;

      // --- Shed title ---
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(shed.name || 'Storage Shed', margin, y);
      y += 8;

      // --- Serial Number ---
      if (serialNumber) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text('Serial Number:', margin, y);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        doc.text(serialNumber, margin + 30, y);
        y += 6;
      }

      // --- Stock Number ---
      if (shed.stockNumber) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text('Stock Number:', margin, y);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        doc.text(shed.stockNumber, margin + 30, y);
        y += 6;
      }

      y += 4;

      // --- Specs table ---
      const specs = [];
      if (shed.shedType) specs.push(['Style', shed.shedType]);
      if (shed.size) specs.push(['Size', shed.size]);
      if (shed.squareFeet) specs.push(['Sq Ft', `${shed.squareFeet} sq ft`]);
      if (shed.material) specs.push(['Material', shed.material]);
      if (shed.roofStyle) specs.push(['Roof Style', shed.roofStyle]);
      if (shed.roofMaterial) specs.push(['Roof Material', shed.roofMaterial]);
      if (shed.sidingType) specs.push(['Siding', shed.sidingType]);
      if (shed.floorType) specs.push(['Floor', shed.floorType]);
      if (shed.doorType) specs.push(['Door', shed.doorType]);
      if (shed.windowType) specs.push(['Windows', shed.windowType]);
      if (shed.condition) specs.push(['Condition', shed.condition]);
      if (shed.yearBuilt) specs.push(['Year Built', String(shed.yearBuilt)]);
      if (shed.warranty) specs.push(['Warranty', shed.warranty]);
      if (shed.warrantyYears) specs.push(['Warranty Years', `${shed.warrantyYears} years`]);

      if (specs.length > 0) {
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(margin, y, pw - margin * 2, 8, 1, 1, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60, 60, 60);
        doc.text('SPECIFICATIONS', margin + 4, y + 5.5);
        y += 12;

        const colW = (pw - margin * 2) / 2;
        doc.setFontSize(9);
        for (let i = 0; i < specs.length; i++) {
          const col = i % 2;
          const x = margin + col * colW;
          if (col === 0 && i > 0) y += 5.5;

          if (i % 4 < 2) {
            doc.setFillColor(255, 255, 255);
          } else {
            doc.setFillColor(250, 250, 250);
          }

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(120, 120, 120);
          doc.text(specs[i][0] + ':', x + 4, y + 3.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(40, 40, 40);
          const valX = x + 32;
          const specVal = String(specs[i][1]);
          doc.text(specVal.charAt(0).toUpperCase() + specVal.slice(1), valX, y + 3.5);
        }
        y += 10;
      }

      // --- Colors ---
      if (shed.bodyColor || shed.trimColor || shed.roofColor) {
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(margin, y, pw - margin * 2, 8, 1, 1, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60, 60, 60);
        doc.text('COLORS', margin + 4, y + 5.5);
        y += 12;

        doc.setFontSize(9);
        const colors = [];
        if (shed.bodyColor) colors.push(['Body', shed.bodyColor]);
        if (shed.trimColor) colors.push(['Trim', shed.trimColor]);
        if (shed.roofColor) colors.push(['Roof', shed.roofColor]);

        colors.forEach(([label, val]) => {
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(120, 120, 120);
          doc.text(label + ':', margin + 4, y + 3.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(40, 40, 40);
          doc.text(String(val).charAt(0).toUpperCase() + String(val).slice(1), margin + 24, y + 3.5);
          y += 5.5;
        });
        y += 4;
      }

      // --- Features ---
      const features = [];
      if (shed.loft) features.push('Loft');
      if (shed.workbench) features.push('Workbench');
      if (shed.electrical) features.push('Electrical');
      if (shed.insulation) features.push('Insulation');
      if (shed.hasRamp) features.push('Ramp');
      if (shed.hasShelving) features.push('Shelving');
      if (shed.deliveryIncluded) features.push('Free Delivery');
      if (shed.setupIncluded) features.push('Free Setup');

      if (features.length > 0) {
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(margin, y, pw - margin * 2, 8, 1, 1, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60, 60, 60);
        doc.text('INCLUDED FEATURES', margin + 4, y + 5.5);
        y += 12;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(40, 40, 40);
        const featLine = features.join('  •  ');
        doc.text(featLine, margin + 4, y + 3.5);
        y += 10;
      }

      // --- Pricing ---
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pw - margin, y);
      y += 8;

      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin, y, pw - margin * 2, 8, 1, 1, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text('PRICING', margin + 4, y + 5.5);
      y += 14;

      // Full price
      if (shed.salePrice && shed.salePrice < shed.price) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(150, 150, 150);
        doc.text('List Price:', margin + 4, y);
        doc.text(formatPrice(shed.price), pw - margin - 4, y, { align: 'right' });
        y += 6;

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(16, 185, 129);
        doc.text('Sale Price:', margin + 4, y);
        doc.text(formatPrice(shed.salePrice), pw - margin - 4, y, { align: 'right' });
        y += 6;
      } else {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('Total Price:', margin + 4, y);
        doc.text(formatPrice(shed.price), pw - margin - 4, y, { align: 'right' });
        y += 6;
      }

      // Deposit
      doc.setDrawColor(16, 185, 129);
      doc.setFillColor(236, 253, 245); // emerald-50
      doc.setLineWidth(0.4);
      doc.roundedRect(margin, y, pw - margin * 2, 10, 1.5, 1.5, 'FD');
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(6, 95, 70); // emerald-800
      doc.text('Deposit Due (25%):', margin + 4, y + 7);
      doc.text(formatPrice(depositAmount), pw - margin - 4, y + 7, { align: 'right' });
      y += 14;

      // Balance
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      doc.text('Balance due at delivery:', margin + 4, y);
      doc.text(formatPrice((effectivePrice || 0) - depositAmount), pw - margin - 4, y, { align: 'right' });
      y += 12;

      // --- QR section at bottom ---
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pw - margin, y);
      y += 6;

      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin, y, pw - margin * 2, 32, 2, 2, 'F');

      const bottomQrSize = 22;
      doc.addImage(qrDataUrl, 'PNG', margin + 6, y + 5, bottomQrSize, bottomQrSize);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text('Ready to Purchase?', margin + 6 + bottomQrSize + 8, y + 10);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Scan this QR code with your phone to purchase', margin + 6 + bottomQrSize + 8, y + 16);
      doc.text('this exact shed online — 24/7, no salesperson needed.', margin + 6 + bottomQrSize + 8, y + 21);

      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(checkoutUrl, margin + 6 + bottomQrSize + 8, y + 27);

      // --- Footer ---
      const footerY = doc.internal.pageSize.getHeight() - 12;
      doc.setDrawColor(220, 220, 220);
      doc.line(margin, footerY - 4, pw - margin, footerY - 4);
      doc.setFontSize(7);
      doc.setTextColor(170, 170, 170);
      doc.text(`Generated by ShedX  •  ${dateStr}  •  shedx.co`, pw / 2, footerY, { align: 'center' });

      // Save
      const fileName = `SalesOrder-${shed.name?.replace(/\s+/g, '-') || 'Shed'}-${serialNumber || shed.id?.slice(0, 8)}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-5 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white">Generate Sales Order</h2>
              <p className="text-emerald-100 text-sm">Printable PDF with QR code for instant purchase</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <FiX className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="p-6 space-y-5">
            {/* Shed summary */}
            <div className="flex gap-4 bg-gray-50 rounded-lg p-4">
              {shed.images?.[0] && (
                <img src={shed.images[0]} alt="" className="w-20 h-20 rounded-lg object-cover" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{shed.name}</p>
                <p className="text-sm text-gray-500">{shed.size} &middot; {shed.shedType}</p>
                <p className="text-sm font-semibold text-emerald-600 mt-1">{formatPrice(effectivePrice)}</p>
              </div>
            </div>

            {/* Editable serial number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Serial Number
                <span className="text-gray-400 font-normal ml-1">(editable)</span>
              </label>
              <div className="flex gap-2">
                <input
                  ref={serialInputRef}
                  type="text"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  placeholder="Enter serial number..."
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none font-mono text-sm"
                />
                {serialNumber && (
                  <button
                    onClick={() => { setSerialNumber(''); serialInputRef.current?.focus(); }}
                    className="p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    title="Clear"
                  >
                    <FiX className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">This serial number will be printed on the sales order PDF</p>
            </div>

            {/* QR preview */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">QR Code Links To</p>
              <code className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded break-all">{checkoutUrl}</code>
              <p className="text-xs text-gray-400 mt-2">
                If this shed is sold, the QR code page shows a SOLD badge and offers to build an identical one.
              </p>
            </div>

            {/* What's included */}
            <div className="text-sm text-gray-600 space-y-1.5">
              <p className="font-medium text-gray-700">PDF includes:</p>
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                <span className="flex items-center gap-1"><FiCheck className="w-3 h-3 text-emerald-500" /> Shed specifications</span>
                <span className="flex items-center gap-1"><FiCheck className="w-3 h-3 text-emerald-500" /> Color details</span>
                <span className="flex items-center gap-1"><FiCheck className="w-3 h-3 text-emerald-500" /> Pricing breakdown</span>
                <span className="flex items-center gap-1"><FiCheck className="w-3 h-3 text-emerald-500" /> QR code for checkout</span>
                <span className="flex items-center gap-1"><FiCheck className="w-3 h-3 text-emerald-500" /> Serial number</span>
                <span className="flex items-center gap-1"><FiCheck className="w-3 h-3 text-emerald-500" /> Dealer branding</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="px-5 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={generatePDF}
                disabled={generating}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white py-2.5 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              >
                {generating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FiDownload className="w-4 h-4" />
                    Download Sales Order PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SalesOrderPDF;
